import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Quote, Target, Eye, Compass, Brain, Scale, FlaskConical, Droplet, Leaf, Ribbon, Megaphone } from "lucide-react";
import { COLORS } from "@/lib/api";

const VALUES = [
  {
    name: "Centrado en juventudes",
    desc: "Las decisiones, programas y estructuras se diseñan desde y para las realidades de las adolescentes y jóvenes.",
    color: COLORS.magenta,
  },
  {
    name: "Horizontalidad",
    desc: "Relaciones de poder conscientes y estructuras de toma de decisiones que evitan la concentración jerárquica.",
    color: COLORS.orange,
  },
  {
    name: "Rendición de cuentas",
    desc: "Transparencia interna y externa sobre el uso de recursos, impacto y procesos.",
    color: COLORS.yellow,
  },
  {
    name: "Cambio sistémico",
    desc: "Intervención en causas estructurales, no solo en síntomas. Incidencia política como herramienta.",
    color: COLORS.lime,
  },
  {
    name: "Transición de liderazgos",
    desc: "La organización se construye para ser liderada por las generaciones que acompaña.",
    color: COLORS.magenta,
  },
  {
    name: "Compasión",
    desc: "Cuidado del equipo y de las participantes como condición no negociable para el trabajo sostenible.",
    color: COLORS.orange,
  },
];

const ODS = [
  { num: "4", name: "Educación de calidad", color: "#C5192D" },
  { num: "5", name: "Igualdad de género", color: "#FF3A21" },
  { num: "10", name: "Reducción de desigualdades", color: "#DD1367" },
];

const PILLARS = [
  {
    icon: Brain,
    title: "Salud mental",
    color: COLORS.magenta,
    paras: [
      "Juventudes Latentes impulsa espacios seguros y proyectos que promueven el autocuidado, la paz y el bienestar emocional.",
      "Este pilar busca que las jóvenes tengan herramientas para manejar estrés, ansiedad, presión social y emociones, fomentando una cultura de apoyo mutuo y acompañamiento dentro de sus comunidades.",
      "La salud mental se reconoce como una parte esencial del liderazgo y el desarrollo integral.",
    ],
  },
  {
    icon: Scale,
    title: "Equidad de género",
    color: COLORS.orange,
    paras: [
      "Este pilar promueve acciones para cerrar la brecha de género en espacios de toma de decisiones.",
      "Fomentamos que las adolescentes y jóvenes ocupen roles de liderazgo, participen en procesos comunitarios y eleven su voz en temas que impactan sus vidas.",
      "El enfoque central es construir entornos más igualitarios donde todas puedan desarrollarse con las mismas oportunidades.",
    ],
  },
  {
    icon: FlaskConical,
    title: "Interseccionalidad en STEAM",
    color: COLORS.yellow,
    paras: [
      "Trabajamos para reducir la brecha de género en Ciencia, Tecnología, Ingeniería, Artes y Matemáticas.",
      "A través de talleres, mentorías e inspiración, impulsamos que más jóvenes mujeres se involucren en estas áreas tradicionalmente masculinizadas.",
      "La interseccionalidad reconoce que factores como clase, origen, identidad y acceso también influyen en la participación en STEAM.",
    ],
  },
  {
    icon: Droplet,
    title: "Gestión menstrual",
    color: COLORS.lime,
    paras: [
      "Este pilar visibiliza la menstruación como un derecho humano y combate los estigmas que la rodean.",
      "Buscamos que las jóvenes tengan acceso a información, productos menstruales y espacios libres de discriminación.",
      "El objetivo es garantizar que ninguna adolescente sea limitada en su educación o bienestar por su ciclo menstrual.",
    ],
  },
  {
    icon: Leaf,
    title: "Acción climática",
    color: COLORS.magenta,
    paras: [
      "Impulsamos la participación de jóvenes en la defensa del planeta.",
      "Nos enfocamos en crear conciencia sobre la crisis climática, promover soluciones sostenibles y construir una relación más responsable con la naturaleza.",
      "Las jóvenes líderes se movilizan para exigir acciones climáticas justas y proteger a las comunidades más vulnerables.",
    ],
  },
  {
    icon: Ribbon,
    title: "Derechos sexuales y reproductivos",
    color: COLORS.orange,
    paras: [
      "Este pilar defiende el derecho de cada persona a tomar decisiones informadas y libres sobre su propio cuerpo.",
      "Promovemos la educación sexual integral como herramienta para prevenir violencia, embarazos no deseados, desinformación y estigma.",
      "Trabajamos para que las jóvenes tengan acceso a información veraz y apoyos que garanticen su autonomía.",
    ],
  },
  {
    icon: Megaphone,
    title: "Participación ciudadana",
    color: COLORS.yellow,
    paras: [
      "Fomentamos que las adolescentes y jóvenes conozcan sus derechos y se involucren activamente en la vida pública de sus comunidades.",
      "A través de la incidencia, el diálogo con autoridades y la organización colectiva, las jóvenes aprenden a transformar su entorno y a abrir espacios de decisión.",
      "Creemos que una ciudadanía joven, informada y participativa es clave para construir sociedades más justas.",
    ],
  },
];

const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: i * 0.06 } }),
};

const QuienesSomos = () => {
  return (
    <div data-testid="subpage-quienes-somos" className="max-w-5xl mx-auto px-6 md:px-12 py-16">
      <Link
        to="/conocenos"
        data-testid="subpage-back"
        className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink/60 hover:text-brand-ink mb-8"
      >
        <ArrowLeft size={16} /> Conócenos
      </Link>

      {/* Header */}
      <motion.div initial="hidden" animate="show" variants={fade}>
        <span className="inline-block w-16 h-1.5 rounded-sm mb-6" style={{ background: COLORS.magenta }} />
        <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tight text-brand-ink">
          Quiénes somos
        </h1>
      </motion.div>

      {/* Intro */}
      <motion.section
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        variants={fade}
        className="mt-8 max-w-3xl space-y-5 text-lg text-brand-ink/80 leading-relaxed"
      >
        <p>
          Juventudes Latentes es un movimiento liderado por juventudes que capacitan, inspiran y
          conectan con otras jóvenes líderes y activistas por la igualdad de género. Las miembras de
          Juventudes Latentes son apasionadas portavoces de la equidad de género, los derechos
          sexuales y reproductivos, la acción climática, la salud mental y otros temas de injusticia
          social. Poderosas y sin miedo, las jóvenes transforman sus comunidades cuando cuentan con
          las herramientas que les permiten ejercer todo su potencial.
        </p>
        <p>
          Lo segundo que tienes que saber de nosotras es que ponemos a{" "}
          <span className="font-semibold text-brand-ink">las juventudes en el centro</span>. Por
          eso, nuestros programas están dirigidos a chicas de entre 13 y 29 años, principalmente.
          ¡Pero no te preocupes si no estás en este rango de edad! Siempre hay otras formas de
          colaborar con nuestra <span className="font-semibold text-brand-ink">misión</span>.
        </p>
        <div className="bg-white border border-[#E5E5E5] rounded-sm p-6">
          <p className="text-base text-brand-ink/80">
            ¿Quieres sumarte? Escríbenos en la sección de{" "}
            <Link to="/contacto" data-testid="intro-contact-link" className="font-semibold text-brand-magenta hover:underline">
              Contacto
            </Link>{" "}
            o al correo{" "}
            <a href="mailto:comunidad@latentes.mx" data-testid="intro-email-link" className="font-semibold text-brand-magenta hover:underline">
              comunidad@latentes.mx
            </a>
            .
          </p>
        </div>
      </motion.section>

      {/* Propósito */}
      <motion.section
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        variants={fade}
        className="mt-16"
      >
        <div className="flex items-center gap-2 text-brand-magenta">
          <Target size={18} />
          <span className="text-sm uppercase tracking-[0.15em] font-semibold">Propósito</span>
        </div>
        <blockquote className="mt-5 relative">
          <Quote className="absolute -left-1 -top-3 text-brand-magenta/20" size={48} />
          <p className="relative font-heading text-2xl md:text-4xl font-semibold tracking-tight leading-tight text-brand-ink">
            Existimos para que las juventudes{" "}
            <span className="text-gradient">nombren, cuestionen y accionen</span> sobre las
            problemáticas que les atraviesan hoy.
          </p>
        </blockquote>
      </motion.section>

      {/* Visión y Misión */}
      <section className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          custom={0}
          variants={fade}
          className="bg-white border border-[#E5E5E5] rounded-sm p-8"
        >
          <div className="flex items-center gap-2 text-brand-orange">
            <Eye size={18} />
            <span className="text-sm uppercase tracking-[0.15em] font-semibold">Visión</span>
          </div>
          <p className="mt-4 font-heading text-xl md:text-2xl font-semibold tracking-tight text-brand-ink leading-snug">
            Un Sur global más justo y digno para las adolescentes y jóvenes mujeres, que respete su
            autonomía.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          custom={1}
          variants={fade}
          className="bg-white border border-[#E5E5E5] rounded-sm p-8"
        >
          <div className="flex items-center gap-2 text-brand-magenta">
            <Compass size={18} />
            <span className="text-sm uppercase tracking-[0.15em] font-semibold">Misión</span>
          </div>
          <p className="mt-4 font-heading text-xl md:text-2xl font-semibold tracking-tight text-brand-ink leading-snug">
            Acompañamos a las juventudes a nombrar, cuestionar y accionar desde una mirada
            interseccional, en contextos semiurbanos y rurales de América Latina.
          </p>
        </motion.div>
      </section>

      {/* Valores */}
      <section className="mt-20">
        <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-brand-ink">
          Nuestros valores
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          {VALUES.map((v, i) => (
            <motion.div
              key={v.name}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={i}
              variants={fade}
              className="group bg-white border border-[#E5E5E5] rounded-sm p-6 transition-all duration-300 hover:-translate-y-1"
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = v.color)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#E5E5E5")}
            >
              <div className="flex items-start gap-4">
                <span
                  className="mt-1 w-3 h-3 rounded-full flex-shrink-0"
                  style={{ background: v.color }}
                />
                <div>
                  <h3 className="font-heading text-xl font-semibold tracking-tight text-brand-ink">
                    {v.name}
                  </h3>
                  <p className="mt-2 text-brand-ink/70 leading-relaxed">{v.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ODS */}
      <section className="mt-20">
        <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-brand-ink">
          Objetivos de Desarrollo Sostenible
        </h2>
        <p className="mt-3 text-brand-ink/70 max-w-2xl">
          Nuestro trabajo contribuye directamente a la Agenda 2030 de las Naciones Unidas.
        </p>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-5">
          {ODS.map((o, i) => (
            <motion.div
              key={o.num}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={i}
              variants={fade}
              data-testid={`ods-${o.num}`}
              className="rounded-sm p-6 text-white"
              style={{ background: o.color }}
            >
              <span className="font-heading text-5xl font-bold leading-none">{o.num}</span>
              <p className="mt-3 text-lg font-semibold leading-snug">{o.name}</p>
              <span className="mt-3 inline-block text-xs uppercase tracking-[0.15em] font-semibold opacity-80">
                ODS {o.num}
              </span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Pilares */}
      <section className="mt-20">
        <div className="bg-brand-ink rounded-sm p-8 md:p-10">
          <p className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-white leading-snug">
            Los <span className="text-gradient">pilares de Juventudes Latentes</span> son
            fundamentales para el desarrollo de nuevos clubs, ayudando a inspirar a nuevas
            generaciones.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                custom={i % 2}
                variants={fade}
                data-testid={`pilar-${i + 1}`}
                className="group bg-white border border-[#E5E5E5] rounded-sm p-7 transition-all duration-300 hover:-translate-y-1"
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = p.color)}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#E5E5E5")}
              >
                <div className="flex items-center gap-4">
                  <span
                    className="w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0"
                    style={{ background: p.color }}
                  >
                    <Icon size={22} className="text-white" strokeWidth={2.2} />
                  </span>
                  <h3 className="font-heading text-xl md:text-2xl font-semibold tracking-tight text-brand-ink">
                    <span className="text-brand-ink/40 mr-2">{i + 1}.</span>
                    {p.title}
                  </h3>
                </div>
                <div className="mt-4 space-y-3">
                  {p.paras.map((para, j) => (
                    <p key={j} className="text-brand-ink/70 leading-relaxed">
                      {para}
                    </p>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default QuienesSomos;
